<template>
  <div class="centered">
    <!-- Show loading spinner if loading -->
    <!-- <LoadingComponent v-if="isLoading" /> -->
    <!-- Scenario #1: Bank account to charge has been selected. Let them pay. -->
    <div v-if="selectedACHPaymentMethod !== null">
      <select
        v-if="activeACHPaymentMethods.length > 0"
        id="achDropdown"
        v-model="selectedACHPaymentMethod"
        class="saved-ach-select"
      >
        <option :value="null">
          Select Saved ACH Account
        </option>
        <option
          v-for="ach in activeACHPaymentMethods"
          :key="ach.token"
          :value="ach"
        >
          {{ ach.bankName }} - Account Ending in {{ ach.last4 }}
        </option>
      </select>
      <p class="textCenter">
        ACH E-Checks require 3 business days to settle.
      </p>
      <div class="ach-button-container">
        <button class="ach-button">
          <span>Complete Payment</span>
        </button>
        <button
          class="cancel-button"
          @click="toggleAccountForm"
        >
          <span>Enable a new bank account</span>
        </button>
      </div>
    </div>
    <!-- Scenario #2: Awainting microtransfer verification -->
    <div v-else-if="showMicroTransferVerification && showAccountForm === false">
      <!-- Show micro transfer collection if microTransferVerificationPending -->
      <div class="ach-verification-container centered">
        <div v-if="microdepositSubmitted === false">
          <div class="ach-verification-container-header">
            Two small deposits from "PAYPAL" are being made to your bank account. It may take up to one business day for the deposits to arrive. Please enter the amounts of the deposits below.
          </div>
          <div class="ach-verification-container-inputs">
            <div class="ach-form-body-container-row-input">
              <input
                v-model="microTransferAmount1"
                class="ach-form-body-container-row-input-number"
                type="number"
                step="0.01"
                placeholder="Deposit 1"
              >
            </div>
            <div class="ach-form-body-container-row-input">
              <input
                v-model="microTransferAmount2"
                class="ach-form-body-container-row-input-number"
                type="number"
                step="0.01"
                placeholder="Deposit 2"
              >
            </div>
          </div>
          <div class="ach-button-container">
            <button
              class="ach-button"
              @click="submitMicroTransferAmounts"
            >
              <span>Submit</span>
            </button>
          </div>
        </div>
        <div v-else>
          Your bank account is being verified. We will notify you by email once the verification is complete.
          <div
            v-if="activeACHPaymentMethods.length > 0"
            class="marginNormalTop"
          >
            <select
              id="achDropdown"
              v-model="selectedACHPaymentMethod"
              class="saved-ach-select"
            >
              <option :value="null">
                Select Saved ACH Account
              </option>
              <option
                v-for="ach in activeACHPaymentMethods"
                :key="ach.token"
                :value="ach"
              >
                {{ ach.bankName }} - Account Ending in {{ ach.last4 }}
              </option>
            </select>
            <p class="textCenter">
              ACH E-Checks require 3 business days to settle.
            </p>
            <div class="ach-button-container">
              <button
                class="ach-button"
                :disabled="selectedACHPaymentMethod === null"
              >
                <span>Complete Payment</span>
              </button>
              <button
                class="cancel-button"
                @click="toggleAccountForm"
              >
                <span>Enable a new bank account</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Scenario #3: Display the mandate text -->
    <div v-else-if="showMandateText">
      <!-- Show authorization text if account is verified -->
      <div
        v-show="showMandateText"
        class="authorization-text-container"
      >
        <div
          v-if="!vaultPaymentMethod"
          id="single-use-method-mandate-text"
          class="authorization-text"
        >
          By clicking [“Complete Payment”], I authorize Braintree, a service of PayPal, on behalf of Leland Little Auctions, LTD to verify my bank account information using
          bank information and consumer reports and I authorize Leland Little Auctions, LTD to initiate an ACH/electronic debit to my checking/savings account,
          Depository Name: {{ depositoryName }}, Routing Number: {{ achFormValues.routingNumber }} and Account Number: {{ achFormValues.accountNumber }},
          in the amount of ${{ invoice.invoice_balance }} on {{ today }}. I agree the ACH transactions I authorize comply with all applicable laws.
        </div>
        <div
          v-else
          id="multi-use-method-mandate-text"
          class="authorization-text"
        >
          By clicking [“Complete Payment”], I authorize Braintree, a service of PayPal, on behalf of Leland Little Auctions, LTD to verify my bank account information using
          bank information and consumer reports and I authorize Leland Little Auctions, LTD to initiate an ACH/electronic debit to my checking/savings account,
          Depository Name: {{ depositoryName }}, Routing Number: {{ achFormValues.routingNumber }} and Account Number: {{ achFormValues.accountNumber }},
          in the amount of ${{ invoice.invoice_balance }} on {{ today }}. I agree the ACH transactions I authorize comply with all applicable laws.
        </div>
        <div class="mandate-button-container">
          <button
            class="cancel-button"
            @click="cancelMandate"
          >
            Cancel
          </button>
          <button
            class="ach-button"
            @click="startMicroTransferVerification"
          >
            <span>Submit</span>
          </button>
        </div>
      </div>
    </div>
    <!-- Scenario #4: Display form to collect bank account info -->
    <div v-else-if="showAccountForm">
      <!-- Show E-Check form if we need to collect account information -->
      <div class="ach-form-container">
        <div id="ach-form">
          <div id="ach-form-header">
            <div
              v-if="activeACHPaymentMethods.length > 0"
              class="ach-form-header-description"
            >
              <div class="marginSmallBottom">
                Enabling a <span>new</span> bank account will take 2-4 business days. You will enter your account details and go through verification. After your bank account is verified, you may send an ACH E-Check for payment.
              </div>
              <div class="marginSmallBottom">
                To add a bank account, enter the information below.
              </div>
              <div>By clicking submit, you authorize us to debit your account for the amount of your purchase.</div>
            </div>
            <div
              v-else
              class="ach-form-header-description"
            >
              <div>ACH E-Checks require 3 business days to settle.</div>
              <div>Enabling a bank account will take 2-4 business days. You will enter your account details and go through verification. After your bank account is verified, you may send an ACH E-Check for payment. </div>
            </div>
          </div>
          <div class="ach-form-body">
            <div class="ach-form-body-container">
              <div class="ach-form-body-container-row">
                <div class="ach-form-body-container-row-label">
                  Bank Name
                </div>
                <div class="ach-form-body-container-row-input">
                  <input
                    v-model="depositoryName"
                    class="ach-form-body-container-row-input-text"
                    name="bankName"
                  >
                </div>
              </div>
              <div class="ach-form-body-container-row">
                <div class="ach-form-body-container-row-label">
                  Account Type
                </div>
                <div>
                  <select
                    class="ach-form-body-container-row-input-select"
                    name="accountType"
                    @change="onFormInputChange"
                  >
                    <option value="">
                      Select Account Type
                    </option>
                    <option value="checking">
                      Checking
                    </option>
                    <option value="savings">
                      Savings
                    </option>
                  </select>
                </div>
              </div>
              <div class="ach-form-body-container-row">
                <div class="ach-form-body-container-row-label">
                  Ownership Type
                </div>
                <div>
                  <select
                    class="ach-form-body-container-row-input-select"
                    name="ownershipType"
                    @change="onFormInputChange"
                  >
                    <option value="">
                      Select Ownership Type
                    </option>
                    <option value="business">
                      Business
                    </option>
                    <option value="personal">
                      Personal
                    </option>
                  </select>
                </div>
              </div>
              <div class="ach-form-body-container-row">
                <div class="ach-form-body-container-row-label">
                  Account Number
                </div>
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="accountNumber"
                    @change="onFormInputChange"
                  >
                </div>
              </div>
              <div class="ach-form-body-container-row">
                <div class="ach-form-body-container-row-label">
                  Routing Number
                </div>
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="routingNumber"
                    @change="onFormInputChange"
                  >
                </div>
              </div>
              <div
                v-show="isPersonal()"
                class="ach-form-body-container-row"
              >
                <div class="ach-form-body-container-row-label">
                  First Name
                </div>
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="firstName"
                    @change="onFormInputChange"
                  >
                </div>
              </div>
              <div
                v-show="isPersonal()"
                class="ach-form-body-container-row"
              >
                <div class="ach-form-body-container-row-label">
                  Last Name
                </div>
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="lastName"
                    @change="onFormInputChange"
                  >
                </div>
              </div>
              <div
                v-show="isBusiness()"
                class="ach-form-body-container-row"
              >
                <div class="ach-form-body-container-row-label">
                  Business Name
                </div>
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="businessName"
                    @change="onFormInputChange"
                  >
                </div>
              </div>
              <div class="ach-form-body-container-row">
                <div class="ach-form-body-container-row-label">
                  Billing Address
                </div>
              </div>
              <div class="ach-form-body-container-row pad-bottom">
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="streetAddress"
                    placeholder="Street address"
                    @change="onFormInputChange"
                  >
                </div>
              </div>
              <div class="ach-form-body-container-row pad-bottom">
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="extendedAddress"
                    placeholder="Apt, suite, etc."
                    @change="onFormInputChange"
                  >
                </div>
              </div>
              <div class="ach-form-body-container-row pad-bottom">
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="locality"
                    placeholder="City"
                    @change="onFormInputChange"
                  >
                </div>
              </div>
              <div class="ach-form-body-container-row pad-bottom">
                <select
                  class="ach-form-body-container-row-input-select"
                  name="region"
                  @change="onFormInputChange"
                >
                  <option value="">
                    Select State
                  </option>
                  <option
                    v-for="state in STATES"
                    :key="state.code"
                    :value="state.code"
                  >
                    {{ state.name }}
                  </option>
                </select>
              </div>
              <div class="ach-form-body-container-row pad-bottom">
                <div class="ach-form-body-container-row-input">
                  <input
                    class="ach-form-body-container-row-input-text"
                    name="postalCode"
                    placeholder="Postal code"
                    @change="onFormInputChange"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="ach-button-container">
          <button
            class="ach-button"
            :disabled="isVerifying || achFormIsValid === false"
            @click="startAccountVerification"
          >
            <span>Next</span>
          </button>
          <button
            class="cancel-button"
            :disabled="isVerifying"
            @click="toggleAccountForm"
          >
            <span>Cancel</span>
          </button>
        </div>
      </div>
    </div>
    <!-- Scenario #5: Customer has no ACH accounts to select from -->
    <div v-else-if="activeACHPaymentMethods.length === 0">
      <!-- Show option to add bank account -->
      <p class="textCenter">
        ACH E-Checks require 3 business days to settle.
      </p>
      <p>Enabling a bank account will take 2-4 business days. You will enter your account details and go through verification. After your bank account is verified, you may send an ACH E-Check for payment.</p>
      <div class="ach-button-container">
        <button
          class="ach-button"
          @click="startAccountCollection"
        >
          <span>Pay with E-Check</span>
        </button>
      </div>
    </div>
    <!-- Scenario #6: Customer has at least 1 ACH bank account and can add more -->
    <div v-else-if="activeACHPaymentMethods.length > 0">
      <select
        id="achDropdown"
        v-model="selectedACHPaymentMethod"
        class="saved-ach-select"
      >
        <option :value="null">
          Select Saved ACH Account
        </option>
        <option
          v-for="ach in activeACHPaymentMethods"
          :key="ach.token"
          :value="ach"
        >
          {{ ach.bankName }} - Account Ending in {{ ach.last4 }}
        </option>
      </select>
      <p class="textCenter">
        ACH E-Checks require 3 business days to settle.
      </p>
      <div class="ach-button-container">
        <button
          class="ach-button"
          :disabled="selectedACHPaymentMethod === null"
        >
          <span>Complete Payment</span>
        </button>
        <button
          class="cancel-button"
          @click="toggleAccountForm"
        >
          <span>Enable a new bank account</span>
        </button>
      </div>
    </div>

    <!-- Show error message if error -->
    <BraintreeErrorMessageComponent
      v-if="error"
      :error="error"
    />
  </div>
</template>
<script>
import BraintreeError from '../../../../errors/BraintreeError'
import BraintreeErrorMessageComponent from '../../Shared/BraintreeErrorMessageComponent.vue'
import LoadingComponent from '../../Shared/LoadingComponent.vue'
import { USA_STATES } from '@/constants/usa-states'
export default {
	name: 'ECheckComponent',
	components: {
		BraintreeErrorMessageComponent,
		LoadingComponent
	},
	props: ['braintreeInstance', 'invoice', 'disablePaymentMethod', 'deviceData', 'showLoadingMessage', 'showError'],
	data: function () {
		return {
			showECheckButton: true,
			showAccountForm: false,
			showMandateText: false,
			showMicroTransferVerification: false,
			achVerificationId: null,
			microTransferAmount1: null,
			microTransferAmount2: null,
			microdepositSubmitted: false,
			isVerifying: false,
			error: null,
			today: new Date().toLocaleDateString(),
			usBankAccountInstance: null,
			// The bank name, probably received in verification process
			depositoryName: '',
			// The verification form values
			achFormValues: {
				accountType: null,
				accountNumber: 0,
				routingNumber: '',
				ownershipType: null,
				firstName: null,
				lastName: null,
				businessName: null,
				billingAddress: {
					streetAddress: null,
					extendedAddress: null,
					locality: null,
					region: null,
					postalCode: null
				}
			},
			vaultPaymentMethod: false,
			pending_ach: false,
			bank_name: '',
			account_type: '',
			business_name: '',
			account_holder_name: '',
			ownership_type: '',
			activeACHPaymentMethods: [],
			selectedACHPaymentMethod: null,
			STATES: USA_STATES
		}
	},
	computed: {

		achFormIsValid () {
			return this.validateForm()
		},
		/**
     * Get the mandate text
     * We must send this to Braintree and it must match what we showed the customer.
     * @returns {string}
     */
		mandateText () {
			let textContainer
			if (this.vaultPaymentMethod) {
				textContainer = document.getElementById('multi-use-method-mandate-text')
				if (textContainer) {
					return textContainer.innerHTML.trim()
				}
			}
			if (!this.vaultPaymentMethod) {
				textContainer = document.getElementById('single-use-method-mandate-text')
				if (textContainer) {
					return textContainer.innerHTML.trim()
				}
			}
			return null
		}
	},
	watch: {
		selectedACHPaymentMethod: function () {
			if (this.selectedACHPaymentMethod === null) {
				const paymentForm = {
					nonce: null,
					type: 'e_check',
					submitCC: false,
					deviceData: this.deviceData,
					savedPaymentMethod: false // technically this could be true, but really only using this for credit card
				}

				this.$store.dispatch('updatePaymentForm', paymentForm)
			} else {
				const paymentForm = {
					nonce: this.selectedACHPaymentMethod.token,
					type: 'e_check',
					submitCC: false,
					deviceData: self.deviceData,
					savedPaymentMethod: false
				}

				this.$store.dispatch('updatePaymentForm', paymentForm)
			}
		}
	},
	created () {
		this.init()
		this.getPendingACH()
		this.getActiveACHPaymentMethods()
	},
	methods: {
		/**
     * Init the ACH Form
     */
		init: async function () {
			this.showLoadingMessage(true, 'Loading ACH...')
			this.usBankAccountInstance = await window.braintree.usBankAccount.create({
				client: this.braintreeInstance
			}).catch((error) => {
				const exception = new BraintreeError(error)
				this.error = exception.getMessage()
				// We cannot continue with ACH, hide payment method
				this.disable()
			})
			this.showLoadingMessage(false, '')
		},
		getActiveACHPaymentMethods: async function () {
			this.showLoadingMessage(true, 'Loading your account details.')
			let apiPayload = {}
			apiPayload = {}
			apiPayload.apiEndpoint = 'account'
			apiPayload.postData = {}
			apiPayload.postData.action = 'get_active_ach'
			apiPayload.postData.customer_id = this.invoice.customer_id
			const response = await this.$store.dispatch('apiCall', apiPayload).catch((error) => {
				console.error(error)
				this.showLoadingMessage(false, '')
			})
			this.showLoadingMessage(false, '')
			if (!response || response.status !== 200) {
				return
			}

			if (response.data.payload.active_ach_methods === true && response.data.payload.ach_payment_methods.length > 0) {
				this.activeACHPaymentMethods = response.data.payload.ach_payment_methods
			}
		},
		getPendingACH: async function () {
			this.showLoadingMessage(true, 'Reviewing your account.')
			let apiPayload = {}
			apiPayload = {}
			apiPayload.apiEndpoint = 'account'
			apiPayload.postData = {}
			apiPayload.postData.action = 'get_pending_ach'
			apiPayload.postData.customer_id = this.invoice.customer_id
			const response = await this.$store.dispatch('apiCall', apiPayload).catch((error) => {
				console.error(error)
				this.showLoadingMessage(false, '')
			})
			this.showLoadingMessage(false, '')
			if (!response || response.status !== 200) {
				// TODO: handle this error.
				return
			}

			if (response.data.payload.pending_ach === true) {
				this.pending_ach = true
				this.showAccountForm = false
				this.showECheckButton = false
				this.showMandateText = false
				this.showMicroTransferVerification = true

				this.bank_name = response.data.payload.bank_name
				this.account_type = response.data.payload.account_type
				this.business_name = response.data.payload.business_name
				this.account_holder_name = response.data.payload.account_holder_name
				this.ownership_type = response.data.payload.ownership_type
				this.achVerificationId = response.data.payload.verification_id

				if (response.data.payload.submitted_microdeposits === 1) {
					this.microdepositSubmitted = true
				}
			}
		},
		cancelMandate: function () {
			this.showMandateText = false
			this.showAccountForm = true
			this.error = null
		},
		/**
     * Called when user click Pay with E-Check button
     * Will show ACH form
     */
		startAccountCollection: function () {
			this.showECheckButton = false
			this.showAccountForm = true
		},
		toggleAccountForm: function () {
			this.showAccountForm = !this.showAccountForm
			if (this.showAccountForm) {
				this.selectedACHPaymentMethod = null
				this.showECheckButton = false
			}
		},
		/**
     * Called when user click Submit button
     * Will start account verification
     */
		startAccountVerification: function () {
			// Validate the form inputs
			const formIsValid = this.validateForm()
			if (!formIsValid) {
				// Alert what is wrong
				return
			}
			// Form is valid, start account verification
			this.showAccountForm = false
			this.showMandateText = true
		},
		/**
     * Gets a braintree nonce and sends to server to start micro-transfer verification
     * @param {*} bankDetails
     */
		startMicroTransferVerification: async function () {
			this.error = null
			this.accountCollectionInProgress = false

			this.isVerifying = true
			const bankDetails = {
				accountType: this.achFormValues.accountType,
				accountNumber: this.achFormValues.accountNumber,
				routingNumber: this.achFormValues.routingNumber,
				ownershipType: this.achFormValues.ownershipType,
				billingAddress: this.achFormValues.billingAddress
			}
			if (this.isPersonal()) {
				bankDetails.firstName = this.achFormValues.firstName
				bankDetails.lastName = this.achFormValues.lastName
			}
			if (this.isBusiness()) {
				bankDetails.businessName = this.achFormValues.businessName
			}

			this.showLoadingMessage(true, 'Submitting your bank account details, please wait.')
			// Tokenize bankAccount info to get a nonce
			const bankAccountNonce = await this.usBankAccountInstance.tokenize({bankDetails, mandateText: this.mandateText}).catch((error) => {
				this.showLoadingMessage(false, '')

				try {
					console.error(error)
					if (error.details.originalError.details.originalError) {
						this.error = error.details.originalError.details.originalError[0].message
					} else {
						const exception = new BraintreeError(error)
						this.error = exception.getMessage()
					}
				} catch (e) {
					this.error = 'Failed to validate bank account.'
				}
			})

			if (!bankAccountNonce) {
				this.isVerifying = false
				return
			}
			// Send the nonce to the server to start the micro transfer flow
			let apiPayload = {}
			apiPayload = {}
			apiPayload.apiEndpoint = 'shipping'
			apiPayload.postData = {}
			apiPayload.postData.action = 'ach_verification'
			apiPayload.postData.nonce = bankAccountNonce.nonce
			apiPayload.postData.braintreeDeviceData = this.deviceData
			apiPayload.postData.customer_id = this.invoice.customer_id
			apiPayload.postData.invoice_id = this.invoice.invoice_id
			const response = await this.$store.dispatch('apiCall', apiPayload).catch((error) => {
				console.log('Error starting micro transfer verification', error)
				this.error = error
				this.showLoadingMessage(false, '')
			})
			if (!response || response.status !== 200) {
				// TODO: handle this error.
				this.showLoadingMessage(false, '')
				this.isVerifying = false
				return
			}

			this.showLoadingMessage(false, '')

			const responseData = response.data.payload

			if (responseData.status !== 'success') {
				this.showError(true, 'Error', 'Failed to verify bank account.')
			} else {
				if (responseData.ach_method_valid === true) {
					this.achVerificationId = response.data.payload.verification_id
					this.showMandateText = false
					this.showMicroTransferVerification = true
				} else {
					this.showError(true, 'Error', responseData.declined_message)
				}
			}
			this.isVerifying = false
		},
		/**
     * Called when user click Submit button in micro transfer verification flow
     */
		submitMicroTransferAmounts: async function () {
			var self = this
			this.showLoadingMessage(true, 'Confirming the deposit amounts, please wait.')
			let apiPayload = {}
			apiPayload = {}
			apiPayload.apiEndpoint = 'shipping'
			apiPayload.postData = {}
			apiPayload.postData.action = 'ach_verification_micro_transfer'
			apiPayload.postData.data = {
				microTransferAmount1: this.microTransferAmount1,
				microTransferAmount2: this.microTransferAmount2,
				verificationId: this.achVerificationId,
				invoiceId: this.invoice.invoice_id
			}
			// TODO: What else is required in this payload?
			const response = await this.$store.dispatch('apiCall', apiPayload).catch((error) => {
				console.log('Error starting micro transfer verification', error)
				this.error = error
				this.showLoadingMessage(false, '')
			})

			this.showLoadingMessage(false, '')

			if (!response || response.status !== 200) {
				console.error(this.error)
				// TODO: handle this error.
				this.showError(true, 'Error', 'Failed to verify micro transfer amounts.')
				return
			}

			if (response.data.payload.success === true) {
				if (response.data.payload.status === 'verified') {
					await this.getActiveACHPaymentMethods()

					this.showAccountForm = false
					this.showMicroTransferVerification = false

					if (this.activeACHPaymentMethods.length > 0) {
						this.selectedACHPaymentMethod = this.activeACHPaymentMethods[0]
					}

					const paymentForm = {
						nonce: response.data.payload.token,
						type: 'e_check',
						submitCC: false,
						deviceData: self.deviceData
					}

					self.$store.dispatch('updatePaymentForm', paymentForm)
				} else if (response.data.payload.status === 'pending' || response.data.payload.status === 'verifying') {
					this.microTransferAmount1 = null
					this.microTransferAmount2 = null
					this.microdepositSubmitted = true
				}
			} else {
				this.showError(true, 'Error', response.data.payload.message)
			}
		},
		/**
     * Called when form input values change.
     */
		onFormInputChange: function (event) {
			const billingAddressFields = ['streetAddress', 'extendedAddress', 'locality', 'region', 'postalCode']
			if (billingAddressFields.includes(event.target.name)) {
				this.achFormValues.billingAddress[event.target.name] = event.target.value
				return
			}
			this.achFormValues[event.target.name] = event.target.value
		},
		/**
     * Validate the form inputs
     */
		validateForm: function () {
			// Check all required fields are filled
			const required = ['accountType', 'accountNumber', 'routingNumber', 'ownershipType', 'billingAddress']
			const billingAddressRequired = ['streetAddress', 'locality', 'region', 'postalCode']
			if (this.isPersonal()) {
				required.push('firstName', 'lastName')
			}
			if (this.isBusiness()) {
				required.push('businessName')
			}
			for (let i = 0; i < required.length; i++) {
				if (required[i] === 'billingAddress') {
					for (let j = 0; j < billingAddressRequired.length; j++) {
						if (!this.achFormValues[required[i]][billingAddressRequired[j]]) {
							return false
						}
					}
					continue
				}
				if (!this.achFormValues[required[i]]) {
					return false
				}
			}
			return true
		},
		/**
     * If account type is checking, return true
     */
		isChecking: function () {
			return this.achFormValues.accountType === 'checking'
		},
		/**
     * If account type is savings, return true
     */
		isSavings: function () {
			return this.achFormValues.accountType === 'savings'
		},
		/**
     * If ownership type is business, return true
     */
		isBusiness: function () {
			return this.achFormValues.ownershipType === 'business'
		},
		/**
     * If ownership type is personal, return true
     */
		isPersonal: function () {
			return this.achFormValues.ownershipType === 'personal'
		},
		/**
     * To force a payment method to be hidden from list.
     */
		disable: function () {
			this.disablePaymentMethod(this.$options.name)
		}
	}
}
</script>
<style scoped>
.ach-form-body-container-row-input-text {
  border-radius: 0;
  border: none;
  outline: 0;
  width: 100%;
  height: 100%;
}

.ach-form-body-container-row-input {
  width: 100%;
  height: 40px;
  padding: 0 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}
.mandate-button-container {
  margin-top: 20px;
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: row;
}
.mandate-button-container .ach-button {
  width: 48%;
  height: 40px;
  padding: 12px 16px 12px 16px;
  border-radius: 8px;
  gap: 4px;
  background: rgba(106, 166, 175, 1);
  border: 0;
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}
.cancel-button {
  border-radius: 8px;
  border: 1px solid rgba(51, 51, 51, 0.70);
  background: var(--white, #FFF);
  width: 95%;
	max-width: 300px;
}
.mandate-button-container .ach-button:disabled {
  background: #ccc;
}
.mandate-button-container .ach-button span {
  color: white;
  line-height: 40px;
}

.ach-button-container {
  margin-top: 20px;
  text-align: center;
  width: 100%;
}

.ach-button-container p {
  margin-bottom: 16px;
}
.ach-button-container .ach-button {
  max-width: 300px;
  width: 95%;
  height: 40px;
  padding: 12px 16px 12px 16px;
  border-radius: 8px;
  gap: 4px;
  background: rgba(106, 166, 175, 1);
  border: 0;
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

.saved-ach-select {
  background: #DEEBED;
  border: 1px solid #6AA6AF;
}
.ach-button-container .ach-button:disabled {
  background: #a8a9ac;
	border-color: #828487;
	border-width: 2px;
  border-style: solid;
	cursor: initial;
}
.ach-button-container .ach-button span {
  color: white;
  line-height: 40px;
}
.pad-bottom {
  padding-bottom: 10px;
}
.ach-form-header-description {
  text-align: center;
}
.authorization-check {
  width: 64px;
  height: 64px;
  margin: 0 auto;
}
.authorization-text {
  background-color: #F3F3F3;
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 8px;
  margin-top: 16px;
}
.ach-verification-container-inputs {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
.ach-form-body-container-row-input-number, .ach-form-body-container-row-input-number:focus {
  outline: 0;
  border: 0;
  background: white;
}
.ach-verification-container-header {
  text-align: center;
  padding-bottom: 16px;
}

#achDropdown {
  margin-bottom: 32px;
}
</style>
